import _ from 'underscore';

import {assert} from 'Asserts';
import {
  LABELING_STRATEGIES_REQUIRING_NAME,
  RegistryLabelingStrategy,
  TagSchema,
} from 'constants/RegistryConstants';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import {ClientId} from 'notebook/ELNConstants';
import {
  defaultStructuredTableColumnsForRegisterBioentity,
  filterOutNonDefaultColumns,
  getBioentityPrimarySamplesFromStructuredTable,
  getTagSchemaForStructuredTable,
} from 'notebook/integrations/registry/StructuredTableUtilsForRegistration';
import {UNITS_COLUMN_NAME} from 'notebook/structured-tables/columns/ColumnDefinitions';
import isMissingFieldColumn from 'notebook/structured-tables/columns/isMissingFieldColumn';
import isReadOnly from 'notebook/structured-tables/columns/isReadOnly';
import {
  isMeasurementAmountStructuredTableColumn,
  isTagValueStructuredTableColumn,
  isUnitSelectorStructuredTableColumn,
} from 'notebook/structured-tables/columns/TypePredicates';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import MixtureStructuredTableRowUpdater from 'notebook/structured-tables/row-updaters/MixtureStructuredTableRowUpdater';
import {
  BaseStructuredTable,
  EvalRowView,
  PrimarySample,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableReorderLocks} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {
  coerceToPrimitive,
  getBioentityInfoFromTypedValue,
  getIdsFromTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  DEFAULT_MIXTURE_AMOUNT,
  DEFAULT_MIXTURE_UNITS,
  ORDERED_MIXTURE_UNITS_FOR_DISPLAY,
} from 'registry/mixtures/MixtureConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import * as logging from 'util/Logging';

const AMOUNT_COLUMN_NAME = 'Target Amount';
const SINGLE_ROW_INVARIANT_METRIC = 'notebook.mixture_metadata_table.unexpected_row_count';

function checkHasExactlyOneRow(table: MixtureMetadataStructuredTable): void {
  const isSingleRow = table.rows.length === 1;
  if (!isSingleRow) {
    logging.error('Mixture metadata table does not have exactly one row', {
      tags: {rowCount: table.rows.length},
      extra: {
        columnCount: table.columns.length,
        schemaCount: table.schemas.length,
        finalizedRowCount: table.rows.filter((row) => row.isFinalized).length,
        labelingStrategy: table.metadata.labelingStrategy,
        hasParentLinkMixtureField: table.metadata.parentLinkMixtureFieldId != null,
        lockRowCount: table.metadata.lockRowCount ?? false,
        numApiRowsToAdd: table.numApiRowsToAdd ?? 0,
        isConfig: table.isConfig,
        apiId: table.apiId,
        provenanceUuid: table.provenanceUuid,
      },
    });
  }
  // TODO(BNCH-167906): Restore assert once we know how tables fail the single-row invariant.
  checkSoftInvariant(isSingleRow, {
    message: 'Mixture metadata table must have exactly one row!',
    expirationDate: '2026-12-08',
    errorInDevAndTest: true,
    metricName: SINGLE_ROW_INVARIANT_METRIC,
    sendToSentry: false,
  });
}

export function _isParentLinkMixtureFieldColumn(
  column: StructuredTableColumn,
  parentLinkMixtureFieldId: string | null
): boolean {
  if (parentLinkMixtureFieldId == null) {
    return false;
  }
  return isTagValueStructuredTableColumn(column) && column.schemaFieldApiId === parentLinkMixtureFieldId;
}

export function getMixtureAmountColumnIndex(table: MixtureMetadataStructuredTable): number {
  const columnIndex = table.columns.findIndex(
    (column) => column.columnType === TableColumnType.MEASUREMENT_AMOUNT
  );
  assert(
    columnIndex !== -1,
    'Expected MixtureMetadataStructuredTable columns to include a column for the mixture amount'
  );
  return columnIndex;
}

/**
 * The mixture metadata table is rendered as a Single Input table, and edits flow through the standard
 * table cell editor, which persists both
 * `text` (the raw user input) and `typedValue` (the evaluated result) on the cell. This reader
 * is tolerant of `text`: if `text` is non-empty, return it directly. Otherwise fall back to the
 * typedValue, returning DEFAULT_MIXTURE_AMOUNT.stringValue only when the cell is fully empty
 * (both `text === ''` and `typedValue` is null/undefined).
 */
export function getMixtureAmountFromTable(table: MixtureMetadataStructuredTable): string {
  checkHasExactlyOneRow(table);
  const cell = table.rows[0]?.cells[getMixtureAmountColumnIndex(table)];
  const text = cell?.text ?? '';
  if (text !== '') {
    return text;
  }
  const typedValue = cell?.typedValue ?? null;
  if (typedValue == null) {
    return DEFAULT_MIXTURE_AMOUNT.stringValue;
  }
  assert(
    typedValue.type === BaseTypedValueType.STRING,
    `Expected typed value for mixture amount cell to be of type STRING, but was ${typedValue.type}`
  );
  return coerceToPrimitive(typedValue) as string;
}

export function getParentMixtureColumnIndex(
  columns: ReadonlyArray<StructuredTableColumn>,
  tableMetadata: MixtureMetadataStructuredTableMetadata
): number {
  const columnIndex = columns.findIndex((column) =>
    _isParentLinkMixtureFieldColumn(column, tableMetadata.parentLinkMixtureFieldId)
  );
  return columnIndex;
}

function getParentMixtureIdFromTypedValue(typedValue: TypedValue | null | undefined): string | null {
  if (typedValue == null) {
    return null;
  }
  const ids = getIdsFromTypedValue(typedValue);
  if (ids == null) {
    // The typedValue might be a placeholder introduced via template migration, or some other value which we
    // should ignore in this case.
    return null;
  }
  if (ids.length === 0) {
    return null;
  } else if (ids.length === 1) {
    return ids[0];
  } else {
    assert(false, 'Expected parent link mixture field to be single-select');
  }
}

/**
 * Sentinel returned from {@link getParentMixtureIdFromTable} when the parent mixture cell contains
 * input that cannot be resolved to a single valid mixture ID (for example, partial user text in the
 * Single Input cell, or a typedValue whose shape is not a single-link mixture reference). Callers
 * use this to distinguish "invalid / mid-edit input" from "explicitly cleared" (`null`) and from
 * "valid parent ID selected" (a `string`).
 *
 */
export const INVALID_PARENT_MIXTURE_ID: unique symbol = Symbol('INVALID_PARENT_MIXTURE_ID');

export type ParentMixtureIdFromTableResult = string | null | typeof INVALID_PARENT_MIXTURE_ID;

/**
 * This reader does not assert that `cell.text` is empty (the Single Input cell editor persists
 * both `text` and `typedValue`) and surfaces invalid inputs via
 * {@link INVALID_PARENT_MIXTURE_ID} instead of collapsing them to `null` (which the reducer
 * treats as "parent was cleared").
 */
function getParentMixtureId(
  mixtureRow: EvalRowView<MixtureMetadataStructuredTableRowMetadata>,
  columns: ReadonlyArray<StructuredTableColumn>,
  tableMetadata: MixtureMetadataStructuredTableMetadata
): ParentMixtureIdFromTableResult {
  const columnIndex = getParentMixtureColumnIndex(columns, tableMetadata);
  if (columnIndex === -1) {
    return null;
  }
  const cell = mixtureRow.cells[columnIndex];
  const text = cell?.text ?? '';
  const typedValue = cell?.typedValue ?? null;
  if (text === '' && typedValue == null) {
    return null;
  }
  if (typedValue != null) {
    const ids = getIdsFromTypedValue(typedValue);
    if (ids != null) {
      if (ids.length === 1) {
        return ids[0];
      }
      if (ids.length === 0 && text === '') {
        return null;
      }
    }
  }
  return INVALID_PARENT_MIXTURE_ID;
}

export function getParentMixtureIdFromTable(
  table: MixtureMetadataStructuredTable
): ParentMixtureIdFromTableResult {
  checkHasExactlyOneRow(table);
  const mixtureRow = table.rows[0];
  if (mixtureRow == null) {
    return null;
  }
  return getParentMixtureId(mixtureRow, table.columns, table.metadata);
}

export function getParentMixtureIdFromTableTypedValueOnly(
  table: MixtureMetadataStructuredTable
): string | null {
  checkHasExactlyOneRow(table);
  const columnIndex = getParentMixtureColumnIndex(table.columns, table.metadata);
  if (columnIndex === -1) {
    return null;
  }
  return getParentMixtureIdFromTypedValue(table.rows[0]?.cells[columnIndex]?.typedValue);
}

/**
 * Returns true if the parent-link mixture cell holds any value at all — either raw `text`
 * entered through the Single Input cell editor, or an evaluated `typedValue`. This is broader
 * than {@link getParentMixtureIdFromTable}, which also considers whether the value resolves to
 * a single mixture link; callers that just need to know whether the user has put *anything* in
 * the cell (e.g. for read-only gating on dependent columns) should use this helper instead.
 */
function isParentLinkMixtureCellNonEmpty(
  mixtureRow: EvalRowView<MixtureMetadataStructuredTableRowMetadata>,
  columns: ReadonlyArray<StructuredTableColumn>,
  tableMetadata: MixtureMetadataStructuredTableMetadata
): boolean {
  const columnIndex = getParentMixtureColumnIndex(columns, tableMetadata);
  if (columnIndex === -1) {
    return false;
  }
  const cell = mixtureRow.cells[columnIndex];
  if (cell == null) {
    return false;
  }
  return cell.text !== '' || cell.typedValue != null;
}

function getColumnForParentMixtureField(
  columns: Array<StructuredTableColumn>,
  parentLinkMixtureFieldId: string
): StructuredTableColumn | undefined {
  return columns.find((column) => _isParentLinkMixtureFieldColumn(column, parentLinkMixtureFieldId));
}

function orderParentMixtureFieldColumnFirst(
  baseColumns: Array<StructuredTableColumn>,
  parentLinkMixtureFieldId: string | null
): Array<StructuredTableColumn> {
  if (parentLinkMixtureFieldId == null) {
    return baseColumns;
  }
  const columnForParentMixtureField = getColumnForParentMixtureField(baseColumns, parentLinkMixtureFieldId);
  assert(
    columnForParentMixtureField != null,
    'Expected column for parent mixture field to exist if parentMixtureFieldId is present'
  );
  const [schemaFieldColumns, otherColumns] = _.partition(baseColumns, (column) =>
    isTagValueStructuredTableColumn(column)
  );
  return [
    ...otherColumns,
    columnForParentMixtureField,
    ...schemaFieldColumns.filter((column) => column !== columnForParentMixtureField),
  ];
}

export interface MixtureMetadataStructuredTableMetadata {
  labelingStrategy: RegistryLabelingStrategy;
  parentLinkMixtureFieldId: string | null;
}
export interface MixtureMetadataStructuredTableRowMetadata extends RowMetadata {}

export default class MixtureMetadataStructuredTableAdapter extends GenericStructuredTableAdapter<
  MixtureMetadataStructuredTableMetadata,
  MixtureMetadataStructuredTableRowMetadata
> {
  getDisplayName(
    table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): string {
    return 'Create mixture preps';
  }

  override allowAsTableMappingDestination(
    _table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): boolean {
    return true;
  }

  override shouldShowAsForm(
    table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): boolean {
    checkHasExactlyOneRow(table);
    return false;
  }

  override shouldSuppressTitleBar(
    _table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): boolean {
    return true;
  }

  /**
   * All of the metadata table's columns are migrated to Single Inputs (see `injectFrontEndMetadata`)
   * so the table can preserve its
   * form-like appearance. Returning true here puts the underlying `TableEditable` into Single
   * Input-only view: the footer, table body, and surrounding border are hidden, and Single
   * Input columns can no longer be converted back to non-single-input columns.
   */
  override shouldRenderAsSingleInputView(
    _table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): boolean {
    return true;
  }

  override enableCustomColumns(
    _table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): boolean {
    return true;
  }

  getBlankRowMetadata(
    tableMetadata: MixtureMetadataStructuredTableMetadata
  ): MixtureMetadataStructuredTableRowMetadata {
    return {};
  }

  override isPositionReadOnly(
    currentColumnIndex: number,
    row: EvalRowView<MixtureMetadataStructuredTableRowMetadata>,
    structuredTable: MixtureMetadataStructuredTable
  ): boolean {
    const {columns, schemas, metadata} = structuredTable;
    const currentColumn = columns[currentColumnIndex];
    if (isReadOnly(currentColumn) || isMissingFieldColumn(currentColumn, schemas)) {
      return true;
    }
    if (
      _isParentLinkMixtureFieldColumn(currentColumn, metadata.parentLinkMixtureFieldId) &&
      row.isFinalized
    ) {
      return true;
    }
    switch (currentColumn.columnType) {
      case TableColumnType.ALIAS:
      case TableColumnType.BIOENTITY_NAME:
        return row.isFinalized;
      case TableColumnType.UNIT_SELECTOR:
        // Lock the Units column whenever the parent-link cell contains a value.
        return isParentLinkMixtureCellNonEmpty(row, columns, metadata);
      default:
        return false;
    }
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: MixtureMetadataStructuredTableMetadata;
  }): StructuredTableColumn {
    if (isUnitSelectorStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {acceptedUnits: ORDERED_MIXTURE_UNITS_FOR_DISPLAY},
      };
    } else if (isMeasurementAmountStructuredTableColumn(column)) {
      return {...column, frontEndMetadata: {allowQS: false}};
    }
    return column;
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: MixtureMetadataStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const baseColumns = orderParentMixtureFieldColumnFirst(
      super._getAllSupportedColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      }),
      metadata.parentLinkMixtureFieldId
    );
    const amountColumn = makeStructuredTableColumn({
      columnType: TableColumnType.MEASUREMENT_AMOUNT,
      name: AMOUNT_COLUMN_NAME,
      isRequired: true,
      defaultValue: {
        typedValue: {
          type: BaseTypedValueType.STRING,
          rawValue: DEFAULT_MIXTURE_AMOUNT.stringValue,
        },
        text: '',
      },
    });
    const unitsColumn = makeStructuredTableColumn({
      columnType: TableColumnType.UNIT_SELECTOR,
      name: UNITS_COLUMN_NAME,
      isRequired: true,
      defaultValue: {
        typedValue: {type: BaseTypedValueType.STRING, rawValue: DEFAULT_MIXTURE_UNITS},
        text: '',
      },
    });
    return [
      // Note that while this includes an ALIAS column, it gets filtered out in getInitialColumns.
      ...defaultStructuredTableColumnsForRegisterBioentity({
        baseColumns,
        bioentityExists: false,
        entityNameColumnConfiguration: LABELING_STRATEGIES_REQUIRING_NAME.includes(metadata.labelingStrategy)
          ? 'REQUIRED'
          : 'ABSENT',
      }),
      amountColumn,
      unitsColumn,
    ];
  }

  override getInitialColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: MixtureMetadataStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return filterOutNonDefaultColumns(
      super.getInitialColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      })
    );
  }

  override toTable(
    structuredTable: StructuredTable<
      MixtureMetadataStructuredTableMetadata,
      MixtureMetadataStructuredTableRowMetadata
    >
  ): Table {
    return {
      ...super.toTable(structuredTable),
      allowAddRows: false,
      lockedDims: {allowAddRows: false, allowRemoveRows: false, allowEditCols: true},
    };
  }

  // Agent keep in sync: MixtureMetadataTableInstantiator.get_primary_sample_ids_for_row (which delegates to
  // get_bioentity_primary_sample_ids) in
  // benchling/notebook/structured_tables/instantiators/mixture_metadata_table_instantiator.py (tested by
  // TestMixtureMetadataInstantiatorPrimarySamples in
  // tests/unit/notebook/structured_tables/instantiators/mixture_metadata_table_instantiator_test.py)
  override getPrimarySamples(
    structuredTable: MixtureMetadataStructuredTable,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    return getBioentityPrimarySamplesFromStructuredTable(structuredTable, computedTable);
  }

  getTagSchema(structuredTable: MixtureMetadataStructuredTable): TagSchema | null {
    return getTagSchemaForStructuredTable(structuredTable);
  }

  getMixtureId(
    table: MixtureMetadataStructuredTable,
    computedTable: Array<Array<EvaluatedCell>> | null
  ): string | null {
    checkHasExactlyOneRow(table);
    if (table.rows.length === 0) {
      return null;
    }
    const bioentityColumnIndex = table.columns.findIndex(
      ({columnType}) => columnType === TableColumnType.EXISTING_BIOENTITY
    );
    assert(
      bioentityColumnIndex !== -1,
      'Expected MixtureMetadataStructuredTable to have an EXISTING_BIOENTITY column'
    );
    const bioentityTypedValue = computedTable?.[0]?.[bioentityColumnIndex]?.value ?? null;
    return bioentityTypedValue ? (getBioentityInfoFromTypedValue(bioentityTypedValue)?.id ?? null) : null;
  }

  override getTableKindRowUpdater(): typeof MixtureStructuredTableRowUpdater {
    return MixtureStructuredTableRowUpdater;
  }

  override allowReorderDimensions(
    structuredTable: StructuredTable<
      MixtureMetadataStructuredTableMetadata,
      MixtureMetadataStructuredTableRowMetadata
    >
  ): TableReorderLocks {
    return {
      [TableDimension.ROW]: false,
      [TableDimension.COLUMN]: false,
    };
  }
}

export type GenericMixtureMetadataStructuredTable<TIds> = BaseStructuredTable<
  MixtureMetadataStructuredTableMetadata,
  MixtureMetadataStructuredTableRowMetadata,
  TIds
>;
export type MixtureMetadataStructuredTable = GenericMixtureMetadataStructuredTable<ClientId>;
